import type { NsDict } from '../ru';

export const lobby: NsDict<'lobby'> = {
  title: 'Lobby',
  intro: 'Forecast the move of a hidden index of three S&P 500 stocks: first only the chart, then the scale, and only in the last round the composition.',
  balance: 'Balance',
  atTables: 'At tables',
  openTables: 'Open tables',
  tables: 'Tables',
  rulesBtn: 'Rules and payout calculation',
  creditsNote: 'Play credits cannot be bought, sold, transferred or exchanged.',
  restore: 'Restore starting balance {amount|cr}',
  'stat.games': 'Games',
  'stat.winRate': 'Win rate',
  'stat.net': 'Net result',
  'stat.avgError': 'Average error',
  profileLink: 'Profile, history and statistics',
  verifyTitle: 'Game verification',
  verifyTextBefore:
    'After every game a participant can download a signed package: all moves, the composition and the source stock quotes, the settlement and the ledger entries. You can verify it here or with the utility',
  verifyTextAfter: 'and compare the quotes with any external source.',
  verifyBtn: 'Verify a package file…',

  flowTitle: 'How a game works',
  'flow.prep': 'Preparation',
  'flow.prepText': 'ante N into the pot, composition hash published',
  'flow.prepTextAnte': 'ante {ante|cr} into the pot, composition hash published',
  'flow.round1': 'Round 1 · chart',
  'flow.round1Text': 'no price scale; choose a direction',
  'flow.round2': 'Round 2 · scale',
  'flow.round2Text': 'prices and distances in points',
  'flow.round2TextWr': 'prices and points; the direction can be changed',
  'flow.round3': 'Round 3 · composition',
  'flow.round3Text': 'three stocks revealed; number of points',
  'flow.round3TextWr': 'three stocks revealed; number of points only',
  'flow.target': 'Target candle',
  'flow.targetText': 'P₁ — close of the next minute',
  'flow.settle': 'Settlement and verification',
  'flow.settleText': 'payouts by accuracy; verification package',
  'flow.settleWr': 'Settlement',
  'flow.settleTextWr': 'those who guessed the direction win; the share depends on accuracy',

  'cat.n10': 'Starter',
  'cat.n50': 'Standard',
  'cat.n200': 'High',
  'cat.anteN': 'ante N',
  'cat.players': 'Players',
  'cat.rounds': 'Rounds',
  'cat.roundsValue': '3 × {s} s',
  'cat.commission': 'Commission',
  'cat.tables': 'Tables',
  'cat.roomsCount': '{rooms} · players {seated}',
  'cat.noOpen': 'none open',
  'cat.newTable': 'A new table will be created.',
  'cat.return': 'Back to the table',
  'cat.sit': 'Take a seat',
  notEnough: 'Not enough credits for the ante.',

  'room.playing': 'game in progress',
  'room.startIn': 'starts in {n} s',
  'room.settling': 'settlement',
  'room.ready': 'ready {ready} of {min}',
  'room.taken': 'taken {seated} of {max}',
  'room.open': 'Open',
  'room.watch': 'Watch',

  'wr.startIn': 'The game starts in {n} s',
  'wr.waitMore': 'Waiting for {n} more confirmation|Waiting for {n} more confirmations',
  'wr.allReady': 'Everyone is ready',
  'wr.preparing': 'Preparing the next game',
  'wr.summary': 'Table {ante|cr} · {min}–{max} players · seated {seated}, ready {ready}',
  'wr.botsNote': 'If there are not enough people, bots will fill the table (they are marked).',
  'wr.spectating': 'You are watching this table.',
  'wr.confirmed': '✓ Participation confirmed.',
  'wr.anteOnStart': 'The ante of {ante|cr} will be charged at the start.',
  'wr.cancel': 'Cancel participation',
  'wr.join': 'Join · ante {ante|cr}',
  'wr.yourBalance': 'Your balance {amount|cr}',
  'wr.seats': 'Seats at the table',
  'wr.you': 'you',
  'wr.gamesCount': 'games: {n}',
  'wr.winRateShort': 'WR',
  'wr.offline': 'offline',
  'wr.ready': 'ready',
  'wr.waiting': 'waiting',
  'wr.free': 'free',

  'rules.title': 'Market Table rules',
  'rules.demo': 'A demo with play credits. There is no real money, no payments, no buying or withdrawing of credits. This is not trading advice.',
  'rules.indexTitle': 'The game index',
  'rules.indexText':
    'Before every game the server randomly picks three different stocks from an open set of liquid S&P 500 stocks. The index is an equally weighted combination of their percentage changes relative to the close of bar P₀:',
  'rules.index1': 'Bᵢ — the stock’s close on bar P₀; the price step is {tick} points; rounding to the nearest step, halves round up.',
  'rules.index2': 'Bar O/C — the index from opens/closes; H/L — from the components’ highs/lows (upper/lower estimate), but never narrower than O and C.',
  'rules.index3': 'A minute without trades in a stock uses its last price.',
  'rules.index4': '“Activity” — the minute volume averaged over the three stocks, divided by the stock’s average volume in the history window (1 = normal). VWAP* is weighted by activity.',
  'rules.index5': 'The formula is public; only the composition of the three is hidden until the third round. The hash commitment is published before betting and revealed after settlement.',
  'rules.flowTitle': 'Course of a game',
  'rules.flow1': 'The ante N goes into the pot, P₀ is fixed.',
  'rules.flow2': 'Round 1: the chart without a scale, choice of direction (up/down) and betting.',
  'rules.flow3': 'Round 2: the price scale and distances in points are revealed. The direction can be kept or changed.',
  'rules.flow4': 'Round 3: the composition is revealed. The direction can no longer change — enter a strictly positive number of points (step {tick}).',
  'rules.flow5': 'Lock-in. P₁ is the close of the next one-minute candle; Δ = P₁ − P₀.',
  'rules.actionsTitle': 'Actions',
  'rules.actionsText':
    'Fold, check, bet, call, raise (at most one raise after the first bet in a round), all-in (with side pots). Time per turn is {s} s; on timeout — check if possible, otherwise fold. At the end of a round only a player whose turn timed out folds. A player who did not get a turn stays in the game: the uncalled part of the bet is returned. A participant without a complete forecast at lock-in is eliminated.',
  'rules.payoutTitle': 'Winners and payouts',
  'rules.payout1': 'A wrong direction always loses, even with an exact number of points.',
  'rules.payout2':
    'For those who guessed: error = |forecast − |Δ||; weight = contribution_to_pot / (1 + error / {tick}). The pot is split in proportion to the weights; remainders of 0.01 cr go by the largest remainder method, ties by turn order.',
  'rules.payout3': 'Each side pot is settled separately; only those who contributed to it and did not fold are eligible. A pot with no correct guesses is returned to its contributors.',
  'rules.payout4': 'Cancellation with a full refund and zero commission: P₁ = P₀; none of the remaining players guessed the direction; no reliable data.',
  'rules.payout5': 'If everyone but one folds, the remaining player takes the pot without a forecast check.',
  'rules.exampleTitle': 'Example',
  'rules.exampleText':
    'Four players, ante 10 cr; Δ = +2.00. Player A: “up 2.00” (error 0, weight 10), B: “up 2.25” (error 0.25 = 1 step, weight 10/2 = 5), C and D — “down”. Pot 40 cr, commission 5% = 2 cr; 38 cr to distribute: A — 25.33 cr, B — 12.67 cr.',
  'rules.commissionTitle': 'Commission',
  'rules.commissionText':
    '{pct}% of the new credits put into the pot in each round (in the first one, together with the antes). Credits put in earlier and returned are not charged; no commission is withheld on cancellation.',
  'rules.capOne': 'one ante',
  'rules.capMany': '{n} ante|{n} antes',
  'rules.cap': 'The cap is {antes} N per round, i.e. at most {max}N per game (like a rake cap in poker).',
  'rules.retained': 'The withheld amount goes to a separate house account of the demo.',
  'rules.hotkeysTitle': 'Keyboard shortcuts',
  'rules.hotkeys': '↑/↓ direction · +/− points (Shift ×4) · F fold · C check/call · B bet/raise · A all-in · Enter confirm · Esc cancel',
};
